import { API_ENDPOINTS, API_BASE_URL } from "../config/config";
import apiClient, { apiRequest } from "./apiClient";
import { STORAGE_KEYS } from "../constants/storage";

/**
 * Provider Service
 * Handles all provider-related API calls
 */

/**
 * Fetch Provider Categories
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getProviderCategories = async () => {
  return apiRequest(
    () => apiClient.get(API_ENDPOINTS.PROVIDER_CATEGORIES),
    "Failed to fetch provider categories"
  );
};

/**
 * Fetch Provider Auth Types
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getProviderAuthTypes = async () => {
  return apiRequest(
    () => apiClient.get(API_ENDPOINTS.PROVIDER_AUTH_TYPES),
    "Failed to fetch provider auth types"
  );
};

/**
 * Fetch Providers List
 * @param {Object} params - Query parameters
 *   - offset: integer (minimum: 0) - Pagination offset
 *   - limit: integer (minimum: 1, maximum: 200) - Number of items to return
 *   - is_active: boolean - Filter by active status
 *   - provider_type: string - Filter by provider type
 *   - q: string - Search provider_name/ui_name
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getProviders = async (params = {}) => {
  return apiRequest(
    () => apiClient.get(API_ENDPOINTS.PROVIDERS, { params }),
    "Failed to fetch providers"
  );
};

/**
 * Fetch Provider APIs/Services (grouped by API category)
 * @param {string} providerId - The UUID of the provider
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getProviderApis = async (providerId) => {
  return apiRequest(
    () => apiClient.get(`${API_ENDPOINTS.PROVIDER_APIS}/${providerId}/apis`),
    "Failed to fetch provider APIs"
  );
};

/**
 * Fetch Provider Endpoints by Provider ID
 * @param {string} providerId - Provider ID to fetch endpoints for
 * @returns {Promise<{success: boolean, data?: Array, message?: string}>}
 */
export const getProviderEndpoints = async (providerId) => {
  return apiRequest(
    () =>
      apiClient.get(API_ENDPOINTS.PROVIDER_ENDPOINTS, {
        params: {
          provider_id: providerId,
          offset: 0,
          limit: 100
        }
      }),
    "Failed to fetch provider endpoints"
  );
};

/**
 * Fetch Provider Logo as Blob URL
 * @param {string} providerId - The UUID of the provider
 * @returns {Promise<string|null>} - Blob URL of the logo or null if failed
 */
export const getProviderLogo = async (providerId) => {
  try {
    const token = localStorage.getItem(STORAGE_KEYS.ACCESS_TOKEN);
    if (!token) {
      return null;
    }

    const response = await fetch(
      `${API_BASE_URL}/api/v1/providers/${providerId}/logo`,
      {
        method: "GET",
        headers: {
          Authorization: `Bearer ${token}`,
          Accept: "image/*"
        },
        cache: "no-cache"
      }
    );

    if (!response.ok) {
      return null;
    }

    const blob = await response.blob();
    return URL.createObjectURL(blob);
  } catch (error) {
    console.error("Error fetching provider logo:", error);
    return null;
  }
};
